// hotspot-undocumented: a source file with high git churn that no context/ scaffold
// file mentions. Deterministic; churn comes from git, mention from parsed scaffold.

import { basename } from "node:path";
import type { ParsedFile, Suggestion, SuggestContext, SuggestionProvider } from "../../types.js";

/** Source files we consider documentable — skip scaffold, config, lockfiles, deps. */
function isDocumentableSource(path: string): boolean {
  if (path.startsWith(".")) return false;
  if (/node_modules\/|dist\/|\.git\//.test(path)) return false;
  if (/(^|\/)(package-lock\.json|package\.json|tsconfig\.json)$/.test(path)) return false;
  if (path.endsWith(".md")) return false; // scaffold/docs, not code
  return /\.(ts|tsx|js|jsx|py|go|rs|rb|java|vue|svelte)$/.test(path);
}

/** Combined text of all context/ scaffold files, lowercased. */
function contextText(files: ParsedFile[]): string {
  return files
    .filter((f) => f.source.includes("context/"))
    .map((f) => f.content.toLowerCase())
    .join("\n");
}

export function findHotspots(ctx: SuggestContext): Suggestion[] {
  const docText = contextText(ctx.files);
  const suggestions: Suggestion[] = [];

  for (const [path, commits] of ctx.churn) {
    if (commits < ctx.settings.hotspotMinCommits) continue;
    if (!isDocumentableSource(path)) continue;

    // Mentioned if the full path or the bare filename appears in any context/ file.
    const lower = path.toLowerCase();
    const base = basename(path).toLowerCase();
    if (docText.includes(lower) || docText.includes(base)) continue;

    suggestions.push({
      kind: "hotspot-undocumented",
      title: `Document hotspot: ${path}`,
      rationale: `${path} changed in ${commits} recent commits but is mentioned in no context/ file. Hot, undocumented code forces the agent to re-derive it each session.`,
      estimatedSaving: commits * 20, // churn × rough per-rediscovery cost
      files: [path],
    });
  }

  return suggestions.sort((a, b) => b.estimatedSaving - a.estimatedSaving);
}

export const hotspotUndocumentedProvider: SuggestionProvider = {
  name: "hotspot-undocumented",
  run: findHotspots,
};
